import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, InputLabel, Stack } from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { StyledDarkRedInformationTypography, StyledInformationSpan, StyledSwitch } from "../../../components/commonComponents/StyledComponents/styled";
import AnnouncementLinkDialog from "../../../components/commonComponents/Announcement/AnnouncementLinkDialog";
import { announcementRichTextSx } from "../../../components/commonComponents/Announcement/AnnouncementHtml";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import {
  ANNOUNCEMENT_TYPE_OPTIONS,
  announcementTypeOptionsFromValue,
  formatAnnouncementDate,
  lastEditDisplay,
  serializeAnnouncementTypes,
  validateAnnouncementForm,
} from "../../../api_calls/announcementUtils";
import { saveAnnouncement } from "../../../api_calls/announcementRequests";
import { toast } from "react-toastify";

const AnnouncementFormDialog = ({ open, usedFor, announcement, onClose, onSaved }) => {
  const quillRef = React.useRef(null);
  const [title, setTitle] = React.useState(announcement?.title || "");
  const [description, setDescription] = React.useState(announcement?.description || "");
  const [types, setTypes] = React.useState(announcementTypeOptionsFromValue(announcement?.types || announcement?.type));
  const [published, setPublished] = React.useState(announcement?.published === true);
  const [dirty, setDirty] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = React.useState(false);
  const [linkDialog, setLinkDialog] = React.useState({ open: false, url: "", text: "", range: null });
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      setTitle(announcement?.title || "");
      setDescription(announcement?.description || "");
      setTypes(announcementTypeOptionsFromValue(announcement?.types || announcement?.type));
      setPublished(announcement?.published === true);
      setDirty(false);
      setFieldErrors({});
      setShowCancelConfirmation(false);
      setShowPublishConfirmation(false);
    }
  }, [open, announcement]);

  const modules = React.useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline", "strike"],
          ["blockquote", "link"],
          [{ list: "bullet" }, { list: "ordered" }, { indent: "-1" }, { indent: "+1" }],
          ["clean"],
        ],
        handlers: {
          link: function handleLink() {
            const quill = quillRef.current?.getEditor?.();
            if (!quill) {
              return;
            }
            const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
            let text = "";
            let url = "";
            if (range.length > 0) {
              text = quill.getText(range.index, range.length);
              const format = quill.getFormat(range);
              url = typeof format.link === "string" ? format.link : "";
            }
            setLinkDialog({ open: true, url, text: text.replace(/\n/g, ""), range });
          },
        },
      },
      // matchVisual adds an empty paragraph before headers/lists/quotes every time saved HTML is loaded
      clipboard: { matchVisual: false },
    }),
    []
  );

  const closeForm = () => {
    setDirty(false);
    onClose();
  };

  const requestClose = () => {
    if (dirty) {
      setShowCancelConfirmation(true);
      return;
    }
    closeForm();
  };

  const persistAnnouncement = async () => {
    if (savingRef.current) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const payload = {
      title: title.trim(),
      description,
      type: serializeAnnouncementTypes(types),
      published,
    };
    if (usedFor === "edit" && announcement?.id != null) {
      payload.id = announcement.id;
    }

    try {
      const result = await saveAnnouncement(payload);
      if (result.success === true) {
        toast(usedFor === "edit" ? "Announcement updated successfully" : "Announcement saved successfully", {
          type: "success",
        });
        if (typeof onSaved === "function") {
          onSaved();
        }
        closeForm();
      } else {
        toast(result.message || "Sorry, something went wrong", { type: "error" });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSave = () => {
    const validation = validateAnnouncementForm({ title, description, types });
    setFieldErrors(validation.errors);
    if (validation.error === true) {
      return;
    }
    const wasPublished = announcement?.published === true;
    if (published === true && wasPublished !== true) {
      setShowPublishConfirmation(true);
      return;
    }
    persistAnnouncement();
  };

  const applyLink = ({ url, text }) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      return;
    }
    const range = linkDialog.range || quill.getSelection(true) || { index: quill.getLength(), length: 0 };
    if (range.length > 0) {
      quill.deleteText(range.index, range.length, "user");
    }
    quill.insertText(range.index, text, "link", url, "user");
    quill.setSelection(range.index + text.length, 0, "user");
    setDescription(quill.root.innerHTML);
    setDirty(true);
    setLinkDialog({ open: false, url: "", text: "", range: null });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event) => {
          event.preventDefault();
        }}
        fullWidth
        maxWidth="md"
        disableScrollLock
      >
        <DialogTitle>{usedFor === "edit" ? "Edit Announcement" : "New Announcement"}</DialogTitle>
        <DialogContent>
          <TextInputField
            fieldRequired={true}
            elemId="announcementTitle"
            label="Title"
            fieldName="announcement_title"
            defaultVal={title}
            inputType="text"
            onChangeCall={(event) => {
              setTitle(event.target.value);
              setDirty(true);
            }}
            fieldError={fieldErrors.title || ""}
          />
          <Box height={16} />
          <InputLabel shrink sx={{ color: "#1E1E28", fontWeight: "bold" }}>
            Announcement text *
          </InputLabel>
          <Box sx={{ "& .ql-editor": announcementRichTextSx }}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={description}
              onChange={(content, _delta, source) => {
                setDescription(content);
                if (source === "user") {
                  setDirty(true);
                }
              }}
              modules={modules}
              formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "indent", "link"]}
            />
          </Box>
          {typeof fieldErrors.description !== "undefined" && fieldErrors.description !== "" && (
            <FormHelperText sx={{ color: "#AF3241" }}>{fieldErrors.description}</FormHelperText>
          )}
          <Box height={12} />
          <MultiSelect
            elemId="announcementTypes"
            label="Type*"
            enableFullWidth={true}
            disableSearch={true}
            showSelectedValues={true}
            options={ANNOUNCEMENT_TYPE_OPTIONS}
            defaultVal={types}
            selectionCheck={(option, value) => option.id === value.id}
            onChangeCall={(selectedOptions) => {
              setTypes(selectedOptions);
              setDirty(true);
            }}
            fieldError={fieldErrors.types || ""}
          />
          <Box height={20} />
          <FormControlLabel
            control={
              <StyledSwitch
                checked={published}
                name="published_to_users"
                onChange={(event) => {
                  setPublished(event.target.checked);
                  setDirty(true);
                }}
              />
            }
            labelPlacement="end"
            label="Published to Users"
          />
          {usedFor === "edit" && (
            <>
              <Box height={35} />
              <StyledDarkRedInformationTypography variant="body2">
                Last edit:
                <StyledInformationSpan
                  style={{
                    color: "#2D2D37",
                    fontWeight: "bold",
                    marginLeft: "5px",
                  }}
                >
                  {lastEditDisplay(announcement, formatAnnouncementDate)}
                </StyledInformationSpan>
              </StyledDarkRedInformationTypography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={2}>
            <CloseButton
              onClick={(event) => {
                event.preventDefault();
                requestClose();
              }}
              label="Cancel"
              icon={<Close />}
            />
            <ActionButton
              onClick={(event) => {
                event.preventDefault();
                if (!saving) {
                  handleSave();
                }
              }}
              label="Save"
              icon={<Save />}
            />
          </Stack>
        </DialogActions>
      </Dialog>

      <AnnouncementLinkDialog
        open={linkDialog.open}
        url={linkDialog.url}
        text={linkDialog.text}
        onClose={() => setLinkDialog({ open: false, url: "", text: "", range: null })}
        onApply={applyLink}
      />

      {showCancelConfirmation === true && (
        <Confirmation
          confirmationTitle="Confirmation"
          confirmationMessage="Are you certain you want to discard all the changes you have made?"
          confirmationActionCall={closeForm}
          closePopup={() => setShowCancelConfirmation(false)}
          showConfirmationPopup={showCancelConfirmation}
        />
      )}

      {showPublishConfirmation === true && (
        <Confirmation
          confirmationTitle="Confirmation"
          confirmationMessage="You are publishing this announcement to all users. Users will be notified that a new announcement is available to read. Are you sure?"
          confirmationActionCall={persistAnnouncement}
          closePopup={() => setShowPublishConfirmation(false)}
          showConfirmationPopup={showPublishConfirmation}
        />
      )}
    </>
  );
};

export default AnnouncementFormDialog;
